import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  // Page styles
  page: {
    fontFamily: 'Helvetica',
    padding: 25,
    backgroundColor: 'white',
  },
  
  // Header styles
  headerContainer: {
    alignItems: 'center',
    marginBottom: 12,
  },
  logo: {
    width: 140,
    height: 55,
    objectFit: 'contain',
    marginBottom: 8,
  },
  companyTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'black',
    textAlign: 'center',
    marginBottom: 6,
    letterSpacing: 0.5,
  },
  documentTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'black',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 0.5,
  },
  
  // Info box - Light pink/rose with dark maroon left border accent
  infoBox: {
    backgroundColor: '#F5E6E8',
    padding: 10,
    marginBottom: 12,
    borderLeft: '4 solid #8B4557',
  },
  infoText: {
    fontSize: 7.5,
    lineHeight: 1.5,
    color: '#000',
    marginBottom: 4,
  },
  infoTextBold: {
    fontSize: 7.5,
    fontWeight: 'bold',
  },
  
  // Table styles
  table: {
    border: '1 solid #000',
    marginBottom: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #000',
    minHeight: 28,
  },
  tableRowNoBorder: {
    flexDirection: 'row',
    minHeight: 28,
  },
  tableRowLast: {
    flexDirection: 'row',
    minHeight: 28,
  },
  tableLabelCell: {
    width: '28%',
    padding: 5,
    borderRight: '1 solid #000',
    justifyContent: 'center',
    backgroundColor: '#D6EAF8',
  },
  tableValueCell: {
    width: '72%',
    padding: 5,
    justifyContent: 'center',
  },
  tableLabelText: {
    fontSize: 8,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  tableLabelSubText: {
    fontSize: 6.5,
    fontStyle: 'italic',
    marginTop: 1,
    color: '#333',
  },
  tableValueText: {
    fontSize: 8,
  },
  
  // Room legend and grid - Matching screenshot colors exactly
  legendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  legendLabel: {
    fontSize: 7.5,
    marginRight: 6,
  },
  legendBox: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    marginRight: 3,
    marginBottom: 2,
  },
  legendText: {
    fontSize: 6.5,
    color: 'white',
  },
  roomsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  roomBox: {
    width: 38,
    height: 28,
    borderWidth: 1,
    borderColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 3,
    marginBottom: 3,
  },
  roomNumber: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#C41E3A',
  },
  
  // Page 2 styles - Lime green matching screenshot
  yellowHeader: {
    backgroundColor: '#C5E26D',
    padding: 6,
    borderBottom: '1 solid #000',
  },
  yellowHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  yellowHeaderSubText: {
    fontSize: 7.5,
    fontStyle: 'italic',
    fontWeight: 'normal',
  },
  maintenanceBox: {
    border: '1 solid #000',
    minHeight: 220,
    marginBottom: 12,
  },
  maintenanceContent: {
    padding: 8,
    minHeight: 200,
  },
  maintenanceText: {
    fontSize: 7.5,
    lineHeight: 1.5,
  },
  
  // Installations table
  installationsTable: {
    border: '1 solid #000',
    marginBottom: 12,
  },
  installationsRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #000',
    minHeight: 22,
  },
  installationsRowLast: {
    flexDirection: 'row',
    minHeight: 22,
  },
  installationsCell: {
    width: '35%',
    padding: 5,
    borderRight: '1 solid #000',
    fontSize: 7.5,
    justifyContent: 'center',
  },
  installationsValueCell: {
    width: '15%',
    padding: 5,
    borderRight: '1 solid #000',
    fontSize: 8,
    textAlign: 'center',
    justifyContent: 'center',
  },
  installationsValueCellLast: {
    width: '15%',
    padding: 5,
    fontSize: 8,
    textAlign: 'center',
    justifyContent: 'center',
  },
  
  // General Utilities
  utilitiesBox: {
    border: '1 solid #000',
    marginBottom: 12,
  },
  utilitiesContent: {
    padding: 8,
  },
  utilitiesText: {
    fontSize: 7.5,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  utilitiesPhotosRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  utilityPhoto: {
    width: 120,
    height: 90,
    objectFit: 'cover',
    marginRight: 8,
  },
  utilityPhotoPlaceholder: {
    width: 120,
    height: 90,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1 solid #ccc',
    marginRight: 8,
  },
  
  // Utility Bills table
  utilityBillsTable: {
    border: '1 solid #000',
    marginBottom: 12,
  },
  utilityBillsRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #000',
    minHeight: 28,
  },
  utilityBillsRowLast: {
    flexDirection: 'row',
    minHeight: 28,
  },
  utilityBillsLabelCell: {
    width: '28%',
    padding: 5,
    borderRight: '1 solid #000',
    justifyContent: 'center',
  },
  utilityBillsLabelCellYellow: {
    width: '28%',
    padding: 5,
    borderRight: '1 solid #000',
    backgroundColor: 'yellow',
    justifyContent: 'center',
  },
  utilityBillsValueCell: {
    width: '36%',
    padding: 5,
    borderRight: '1 solid #000',
    fontSize: 7.5,
    justifyContent: 'center',
  },
  utilityBillsValueCellLast: {
    width: '36%',
    padding: 5,
    fontSize: 7.5,
    justifyContent: 'center',
  },
  utilityBillsLabelText: {
    fontSize: 7.5,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  utilityBillsSubText: {
    fontSize: 6.5,
    fontStyle: 'italic',
    color: '#D35400',
  },
  
  // Accountability section - Light blue matching screenshot
  accountabilityBox: {
    border: '1 solid #000',
    marginBottom: 12,
  },
  accountabilityHeader: {
    backgroundColor: '#B4D5E8',
    padding: 6,
    borderBottom: '1 solid #000',
  },
  accountabilityHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  accountabilityRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #000',
    minHeight: 22,
  },
  accountabilityRowLast: {
    flexDirection: 'row',
    minHeight: 22,
  },
  accountabilityCell: {
    width: '33.33%',
    padding: 5,
    borderRight: '1 solid #000',
    justifyContent: 'center',
  },
  accountabilityCellLast: {
    width: '33.33%',
    padding: 5,
    justifyContent: 'center',
  },
  accountabilityLabelText: {
    fontSize: 7.5,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  accountabilityValueText: {
    fontSize: 7.5,
    textAlign: 'center',
  },
  
  // Footer - Teal color matching screenshot
  footerText: {
    fontSize: 7.5,
    textAlign: 'center',
    color: '#1B8AAE',
    marginTop: 8,
  },
  footerInfo: {
    fontSize: 6.5,
    textAlign: 'center',
    color: '#1B8AAE',
    marginTop: 2,
  },
  
  // Page 3 - Additional Notes - Orange matching screenshot
  orangeHeader: {
    backgroundColor: '#E08850',
    padding: 6,
    borderBottom: '1 solid #000',
  },
  orangeHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  additionalNotesBox: {
    border: '1 solid #000',
    minHeight: 680,
  },
  additionalNotesContent: {
    padding: 8,
  },
  additionalNotesText: {
    fontSize: 7.5,
    lineHeight: 1.5,
  },
});

// Room size colors mapping - matching Laravel exactly (from config/property.php)
const roomSizeColors = {
  // String keys (from form)
  'single': 'rgb(149,179,215)',      // Single - Light Blue
  'double': 'rgb(227,108,10)',       // Double - Orange/Red
  'large': 'rgb(49,132,155)',        // Large - Teal
  'very_large': 'rgb(146,208,80)',   // Very Large - Green
  'unit': '#b49c30',                 // Unit - Gold/Brown
  'flat': '#787878',                 // Flat - Gray
  // Numeric keys as strings (from database JSON parsing)
  '0': 'rgb(149,179,215)',           // Single - Light Blue
  '1': 'rgb(227,108,10)',            // Double - Orange/Red
  '2': 'rgb(49,132,155)',            // Large - Teal
  '3': 'rgb(146,208,80)',            // Very Large - Green
  '4': '#b49c30',                    // Unit - Gold/Brown
  '5': '#787878',                    // Flat - Gray
  '': 'white',
  'null': 'white',
  'undefined': 'white',
};

const roomSizeLabels = {
  single: 'Single',
  double: 'Double',
  large: 'Large',
  very_large: 'Very Large',
  unit: 'Unit',
  flat: 'Flat',
};

const InitialInspectionPDF = ({ data }) => {
  if (!data) return null;

  // Get room color based on size - handles numeric and string values
  const getRoomColor = (roomSize) => {
    if (roomSize === null || roomSize === undefined || roomSize === '') {
      return 'white';
    }
    // Convert to string for consistent lookup
    const key = String(roomSize);
    return roomSizeColors[key] || 'white';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('/')) return dateString;
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error('Date parsing error:', e);
    }
    return dateString;
  };

  // Parse rooms data - handle array, object with numeric keys, or JSON string
  let parsedRooms = [];
  if (data.rooms) {
    if (Array.isArray(data.rooms)) {
      parsedRooms = data.rooms;
    } else if (typeof data.rooms === 'object') {
      // Convert object with numeric keys to array
      const maxKey = Math.max(...Object.keys(data.rooms).map(k => parseInt(k, 10)));
      parsedRooms = Array.from({ length: maxKey + 1 }, (_, i) => data.rooms[i] ?? data.rooms[String(i)] ?? null);
    } else if (typeof data.rooms === 'string') {
      try {
        parsedRooms = JSON.parse(data.rooms);
      } catch (e) {
        console.error('Error parsing rooms JSON:', e);
        parsedRooms = [];
      }
    }
  }
  // Use rooms_number to limit displayed rooms (database may have stale entries)
  const roomsNumber = data.rooms_number || parsedRooms.length || 0;
  // Only display rooms up to the configured rooms_number
  const rooms = roomsNumber > 0 ? parsedRooms.slice(0, roomsNumber) : [];

  return (
    <Document>
      {/* Page 1: Property Details */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.logo}
          />
          <Text style={styles.companyTitle}>GLOBAL GUARDIANS MANAGEMENT LTD</Text>
          <Text style={styles.documentTitle}>INITIAL INSPECTION</Text>
        </View>

        {/* Green Info Box */}
        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            Before taking any property under our management the Account Manager must fully complete the <Text style={styles.infoTextBold}>Initial Inspection</Text> of the premises. This needs to detail every aspect of the building so that the Account Manager can formulate a comprehensive and fit-for-purpose Property Protection Proposal. Spend as long on site as necessary so that you do not waste time returning to site to check what you have missed.
          </Text>
          <Text style={styles.infoText}>
            Document also serves as essential information shared with Operations in order for team to attend site in one visit and carry out works required.
          </Text>
        </View>

        {/* Property Details Table */}
        <View style={styles.table}>
          {/* Property Address */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>PROPERTY ADDRESS:</Text>
              <Text style={styles.tableLabelSubText}>(Include any specific location details)</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.address || ''}</Text>
            </View>
          </View>

          {/* Property Owner */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>PROPERTY OWNER:</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.client || ''}</Text>
            </View>
          </View>

          {/* What Council */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>WHAT COUNCIL:</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.what_council || ''}</Text>
            </View>
          </View>

          {/* Building Type */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>BUILDING TYPE:</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.building_type || ''}</Text>
            </View>
          </View>

          {/* Property for VOA */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>PROPERTY FOR VOA:</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.property_for_voa ? 'Yes' : 'No'}</Text>
            </View>
          </View>

          {/* How Many Floors */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>HOW MANY FLOORS:</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.floors || ''}</Text>
            </View>
          </View>

          {/* How Many Guardians/Guardian Rooms */}
          <View style={[styles.tableRow, { minHeight: 100 }]}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>HOW MANY GUARDIANS/GUARDIAN ROOMS:</Text>
            </View>
            <View style={styles.tableValueCell}>
              {/* Legend - colors matching Laravel config/property.php */}
              <View style={styles.legendContainer}>
                <Text style={styles.legendLabel}>Legend:</Text>
                <View style={[styles.legendBox, { backgroundColor: 'rgb(149,179,215)' }]}>
                  <Text style={[styles.legendText, { color: '#000' }]}>Single</Text>
                </View>
                <View style={[styles.legendBox, { backgroundColor: 'rgb(227,108,10)' }]}>
                  <Text style={styles.legendText}>Double</Text>
                </View>
                <View style={[styles.legendBox, { backgroundColor: 'rgb(49,132,155)' }]}>
                  <Text style={styles.legendText}>Large</Text>
                </View>
                <View style={[styles.legendBox, { backgroundColor: 'rgb(146,208,80)' }]}>
                  <Text style={[styles.legendText, { color: '#000' }]}>Very Large</Text>
                </View>
                <View style={[styles.legendBox, { backgroundColor: '#b49c30' }]}>
                  <Text style={styles.legendText}>Unit</Text>
                </View>
                <View style={[styles.legendBox, { backgroundColor: '#787878' }]}>
                  <Text style={styles.legendText}>Flat</Text>
                </View>
              </View>
              {/* Room Grid - only show configured rooms (matching Laravel behavior) */}
              <View style={styles.roomsGrid}>
                {rooms.length > 0 ? (
                  rooms.map((roomSize, i) => {
                    const bgColor = getRoomColor(roomSize);
                    const hasRoom = roomSize !== null && roomSize !== undefined && roomSize !== '';
                    // Use white text for darker backgrounds (Double-orange, Large-teal, Flat-gray)
                    const sizeKey = String(roomSize);
                    const needsWhiteText = sizeKey === '1' || sizeKey === 'double' ||
                                          sizeKey === '2' || sizeKey === 'large' ||
                                          sizeKey === '5' || sizeKey === 'flat';
                    const textColor = hasRoom ? (needsWhiteText ? '#fff' : '#000') : '#000';
                    return (
                      <View key={i} style={[styles.roomBox, { backgroundColor: bgColor }]}>
                        <Text style={[styles.roomNumber, { color: textColor }]}>{i + 1}</Text>
                      </View>
                    );
                  })
                ) : (
                  // Fallback: show roomsNumber boxes if rooms array is empty
                  Array.from({ length: roomsNumber }, (_, i) => (
                    <View key={i} style={[styles.roomBox, { backgroundColor: 'white' }]}>
                      <Text style={[styles.roomNumber, { color: '#000' }]}>{i + 1}</Text>
                    </View>
                  ))
                )}
              </View>
            </View>
          </View>

          {/* How Many Toilets */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>HOW MANY TOILETS:</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.toilets || ''}</Text>
            </View>
          </View>

          {/* How Many Bathrooms */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>HOW MANY BATHROOMS:</Text>
              <Text style={styles.tableLabelSubText}>Locations</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.bathrooms || ''}</Text>
            </View>
          </View>

          {/* How Many Kitchens */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>HOW MANY KITCHENS:</Text>
              <Text style={styles.tableLabelSubText}>Locations</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.kitchens || ''}</Text>
            </View>
          </View>

          {/* Is There Parking */}
          <View style={styles.tableRow}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>IS THERE PARKING:</Text>
              <Text style={styles.tableLabelSubText}>Car park or free parking on street</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.parking ? 'Yes' : 'No'}</Text>
            </View>
          </View>

          {/* Is There a Garden */}
          <View style={styles.tableRowLast}>
            <View style={styles.tableLabelCell}>
              <Text style={styles.tableLabelText}>IS THERE A GARDEN:</Text>
              <Text style={styles.tableLabelSubText}>Size</Text>
            </View>
            <View style={styles.tableValueCell}>
              <Text style={styles.tableValueText}>{data.garden ? 'Yes' : 'No'}</Text>
            </View>
          </View>
        </View>
      </Page>

      {/* Page 2: Maintenance, Installations, Utilities */}
      <Page size="A4" style={styles.page}>
        {/* Maintenance & Notes */}
        <View style={styles.maintenanceBox}>
          <View style={styles.yellowHeader}>
            <Text style={styles.yellowHeaderText}>MAINTENANCE & NOTES:</Text>
          </View>
          <View style={styles.maintenanceContent}>
            <Text style={styles.maintenanceText}>{data.maintenance || ''}</Text>
          </View>
        </View>

        {/* Installations Table */}
        <View style={styles.installationsTable}>
          <View style={styles.yellowHeader}>
            <Text style={styles.yellowHeaderText}>
              INSTALLATIONS <Text style={styles.yellowHeaderSubText}>(How Many to be installed)</Text>
            </Text>
          </View>
          
          {/* Row 1 */}
          <View style={styles.installationsRow}>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Shower Cubicles</Text>
            <Text style={styles.installationsValueCell}>{data.installation_shower_cubicles || 0}</Text>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Kitchens (worktops)</Text>
            <Text style={styles.installationsValueCellLast}>{data.installation_kitchens || 0}</Text>
          </View>
          
          {/* Row 2 */}
          <View style={styles.installationsRow}>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Shower electric units</Text>
            <Text style={styles.installationsValueCell}>{data.installation_shower_electric_units || 0}</Text>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Electric Heaters</Text>
            <Text style={styles.installationsValueCellLast}>{data.installation_electric_heaters || 0}</Text>
          </View>
          
          {/* Row 3 */}
          <View style={styles.installationsRow}>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Over sink water heaters</Text>
            <Text style={styles.installationsValueCell}>{data.installation_over_sink_water_heaters || 0}</Text>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Fridges</Text>
            <Text style={styles.installationsValueCellLast}>{data.installation_fridges || 0}</Text>
          </View>
          
          {/* Row 4 */}
          <View style={styles.installationsRow}>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Mini Ovens</Text>
            <Text style={styles.installationsValueCell}>{data.installation_mini_ovens || 0}</Text>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Cookers</Text>
            <Text style={styles.installationsValueCellLast}>{data.installation_cookers || 0}</Text>
          </View>
          
          {/* Row 5 */}
          <View style={styles.installationsRowLast}>
            <Text style={[styles.installationsCell, {backgroundColor: '#b6c999'}]}>Microwaves</Text>
            <Text style={styles.installationsValueCell}>{data.installation_microwaves || 0}</Text>
            <Text style={styles.installationsCell}></Text>
            <Text style={styles.installationsValueCellLast}></Text>
          </View>
        </View>

        {/* General Utilities */}
        <View style={styles.utilitiesBox}>
          <View style={[styles.yellowHeader, {backgroundColor: 'yellow'}]}>
            <Text style={styles.yellowHeaderText}>GENERAL UTILITIES:</Text>
          </View>
          <View style={styles.utilitiesContent}>
            <Text style={styles.utilitiesText}>{data.locations_of_meters || ''}</Text>
            <View style={styles.utilitiesPhotosRow}>
              {data.image_gas_meters_url ? (
                <Image src={data.image_gas_meters_url} style={styles.utilityPhoto} />
              ) : (
                <View style={styles.utilityPhotoPlaceholder}>
                  <Text style={{ fontSize: 7, color: '#999' }}>Gas Meters</Text>
                </View>
              )}
              {data.image_electric_meters_url ? (
                <Image src={data.image_electric_meters_url} style={styles.utilityPhoto} />
              ) : (
                <View style={styles.utilityPhotoPlaceholder}>
                  <Text style={{ fontSize: 7, color: '#999' }}>Electric Meters</Text>
                </View>
              )}
              {data.image_water_meters_url ? (
                <Image src={data.image_water_meters_url} style={styles.utilityPhoto} />
              ) : (
                <View style={styles.utilityPhotoPlaceholder}>
                  <Text style={{ fontSize: 7, color: '#999' }}>Water Meters</Text>
                </View>
              )}
              {data.image_fuse_boards_url ? (
                <Image src={data.image_fuse_boards_url} style={styles.utilityPhoto} />
              ) : (
                <View style={styles.utilityPhotoPlaceholder}>
                  <Text style={{ fontSize: 7, color: '#999' }}>Fuse Boards</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Utility Bills Table */}
        <View style={styles.utilityBillsTable}>
          <View style={styles.utilityBillsRow}>
            <View style={styles.utilityBillsLabelCellYellow}>
              <Text style={styles.utilityBillsLabelText}>UTILITY BILLS:</Text>
              <Text style={styles.utilityBillsSubText}>(be specific to what bills)</Text>
            </View>
            <Text style={styles.utilityBillsValueCell}>CLIENT: {data.utility_bills_client || ''}</Text>
            <Text style={styles.utilityBillsValueCellLast}>GGM: {data.utility_bills_ggm || ''}</Text>
          </View>
          <View style={styles.utilityBillsRowLast}>
            <View style={styles.utilityBillsLabelCellYellow}>
              <Text style={styles.utilityBillsLabelText}>WASTE COLLECTION:</Text>
            </View>
            <Text style={styles.utilityBillsValueCell}>CLIENT: {data.waste_collection_client || ''}</Text>
            <Text style={styles.utilityBillsValueCellLast}>GGM: {data.waste_collection_ggm || ''}</Text>
          </View>
        </View>

        {/* Accountability */}
        <View style={styles.accountabilityBox}>
          <View style={styles.accountabilityHeader}>
            <Text style={styles.accountabilityHeaderText}>ACCOUNTABILITY:</Text>
          </View>
          <View style={styles.accountabilityRow}>
            <View style={styles.accountabilityCell}>
              <Text style={styles.accountabilityLabelText}>Surveyed by:</Text>
            </View>
            <View style={styles.accountabilityCell}>
              <Text style={styles.accountabilityLabelText}>Signed:</Text>
            </View>
            <View style={styles.accountabilityCellLast}>
              <Text style={styles.accountabilityLabelText}>Date:</Text>
            </View>
          </View>
          <View style={styles.accountabilityRowLast}>
            <View style={styles.accountabilityCell}>
              <Text style={styles.accountabilityValueText}>{data.surveyed_by || ''}</Text>
            </View>
            <View style={styles.accountabilityCell}>
              <Text style={styles.accountabilityValueText}>{data.signed || ''}</Text>
            </View>
            <View style={styles.accountabilityCellLast}>
              <Text style={styles.accountabilityValueText}>{formatDate(data.report_date)}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <Text style={styles.footerText}>Global Guardians Management Ltd – Initial Inspection</Text>
        <Text style={styles.footerInfo}>
          Form: {data.form || 'Q15'}     Issued: {formatDate(data.issued)}     Review date: {formatDate(data.review_date)}     Approved: {data.approved || ''}
        </Text>
      </Page>

      {/* Page 3: Additional Notes */}
      <Page size="A4" style={styles.page}>
        <View style={styles.additionalNotesBox}>
          <View style={styles.orangeHeader}>
            <Text style={styles.orangeHeaderText}>ADDITIONAL NOTES:</Text>
          </View>
          <View style={styles.additionalNotesContent}>
            <Text style={styles.additionalNotesText}>{data.notes || ''}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InitialInspectionPDF;

